import { Injectable } from '@angular/core';
import { interval, Subscription, from, EMPTY } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export interface WhatsnewEntry {
  title: string;
  description: string;
  image?: string | null;
}

export interface WhatsnewData {
  version: string;
  date: string;
  entries: WhatsnewEntry[];
}

// Uses native fetch() to bypass the auth interceptor — both files are public static assets.
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private currentVersion: string | null = null;
  private subscription: Subscription | null = null;
  private visibilityHandler: (() => void) | null = null;

  // Polls version.json every 5 min AND checks instantly on tab refocus.
  start(onNewVersion: () => void): void {
    this._fetchVersion()
      .then((version) => { this.currentVersion = version; })
      .catch(() => {});

    this.subscription = interval(POLL_INTERVAL_MS).pipe(
      switchMap(() => from(this._fetchVersion()).pipe(catchError(() => EMPTY))),
      filter((version) => this.currentVersion !== null && version !== this.currentVersion),
    ).subscribe(() => onNewVersion());

    // Instant check when the user switches back to this tab — fires before the next poll.
    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible' || this.currentVersion === null) { return; }
      this._fetchVersion()
        .then((version) => { if (version !== this.currentVersion) { onNewVersion(); } })
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  // Fetches whatsnew.json — manually edited by the developer before each deploy.
  // Returns changelog data if the user hasn't seen this version's entries yet.
  async checkForWhatsnew(): Promise<WhatsnewData | null> {
    try {
      const data = await this._fetchWhatsnew();
      if (localStorage.getItem('pixo_seen_whatsnew') !== data.version && data.entries?.length > 0) {
        return data;
      }
    } catch {}
    return null;
  }

  markWhatsnewSeen(version: string): void {
    localStorage.setItem('pixo_seen_whatsnew', version);
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private async _fetchVersion(): Promise<string> {
    const response = await fetch(`/version.json?t=${Date.now()}`);
    if (!response.ok) { throw new Error(`version.json: ${response.status}`); }
    const data: { version: string } = await response.json();
    return data.version;
  }

  private async _fetchWhatsnew(): Promise<WhatsnewData> {
    const response = await fetch(`/whatsnew.json?t=${Date.now()}`);
    if (!response.ok) { throw new Error(`whatsnew.json: ${response.status}`); }
    return response.json();
  }
}
